import { Block } from "./shared/Block";
import { useTranslation } from "react-i18next";
import { FileUploadDropzone } from "@/components/upload/FileUploadDropzone";
import { Trash2Icon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { DeleteRowConfirmationDialog } from "@/components/ax/dataTable/DeleteRowConfirmationDialog";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { showSuccess, showApiError } from "@/lib/toast";
import { deleteTemplateById } from "@/sideEffects/api";
import { useMutation } from "@tanstack/react-query";
import { selectActiveFirm } from "@/store/appSelectors";
import { useSelector } from "react-redux";
import { selectCurrentTemplate } from "@/store/templateEditor/templateEditorSelectors";

type Props = {
  imageUrlOption: {
    value: string | null;
    onChange: (value: File) => void;
    onDelete: () => void;
    isLoading: boolean;
  };
};

export const TemplateSettingsInspectorContent: React.FC<Props> = ({ imageUrlOption }) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);
  const currentTemplate = useSelector(selectCurrentTemplate);
  const { goToTemplate } = useAppNavigate();

  const deleteTemplateMutation = useMutation({
    mutationFn: () => deleteTemplateById(String(activeFirm?.firmGuid), String(currentTemplate?.id)),
    onSuccess: () => {
      showSuccess(t("common.deletedSuccess"));
      goToTemplate();
    },
    onError: showApiError,
  });

  return (
    <>
      <Block title={t("template.inspector.templateSettings")}>
        <div className="text-sm mt-4 mb-2">{t("template.inspector.background")}</div>
        {imageUrlOption.value ? (
          <div className="flex items-center justify-between gap-2 p-2 bg-muted rounded-md">
            <img src={imageUrlOption.value} alt="background" className="w-10 h-10" />
            <div className="flex items-center justify-between gap-2">
              <Button onClick={imageUrlOption.onDelete} variant="outline" size="icon">
                <Trash2Icon className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ) : (
          <FileUploadDropzone
            onClickUpload={(files) => imageUrlOption.onChange(files[0])}
            dropzoneOptions={{
              maxFiles: 1,
              accept: {
                "image/*": [".png", ".jpg", ".jpeg"],
              },
            }}
            isLoading={imageUrlOption.isLoading}
          />
        )}
      </Block>
      <Separator />
      {/* Delete template hidden for now */}
      {/* <div className="flex items-center justify-center p-4">
        <DeleteRowConfirmationDialog
          onConfirmDelete={deleteTemplateMutation.mutate}
          isLoading={deleteTemplateMutation.isPending}
          deleteText={t("template.inspector.deleteTemplate")}
        />
      </div> */}
    </>
  );
};
